import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useAgentHub } from '@/hooks/useAgentHub';
import { useActivityFeed } from '@/hooks/useActivityFeed';
import { usePoller } from '@/hooks/usePoller';
import { useConversationStore } from '@/stores/conversationStore';
import { useThemeStore } from '@/stores/themeStore';
import { useLangStore, dirFor } from '@/stores/langStore';
import { makeTokens } from '@/theme/tokens';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { getConsoleSettings } from '@/api/sessions';
import { useAuthStore } from '@/stores/authStore';
import { useAgentStore } from '@/stores/agentStore';
import type { AgentDirectoryEntryDto, ConversationStateDef } from '@/types';
import { ConsoleContext, type ConsoleContextValue } from './ConsoleContext';
import { useSessionLoaders } from './useSessionLoaders';
import { Rail } from '@/components/console/Rail';
import { Topbar } from '@/components/console/Topbar';

/** The counts and the drafts feed are re-read this often while the tab is visible (the pushes bound the staleness, this bounds the missed ones). */
export const COUNTS_POLL_MS = 60_000;

/** A burst of "the console config changed" signals (push + store counter) is coalesced into ONE re-read this long after the last. */
export const CONSOLE_CONFIG_DEBOUNCE_MS = 1000;

/**
 * The authenticated application shell (rail + topbar + routed view). Owns the
 * single /agenthub connection and the session loading, applies the design
 * tokens (theme) + text direction (language) to the .ac-root container, and
 * exposes the hub + the loaders via ConsoleContext to every routed view.
 *
 * ADR-033: the list is PER FOLDER (`useSessionLoaders`): the first page of the selected folder on boot / selection,
 * the next page on `loadMore`, the counts / tag catalog / drafts / consultations beside it, and one `refreshRow`
 * queue as the only way a single conversation is re-read. No client-side window on closed conversations exists any
 * more: "All closed" lists every closed conversation the server lists.
 */
export const ConsoleShell: React.FC = () => {
  // The hub is created BEFORE the loaders (the loaders read `hub.status`), but it must call into them: the ref is the
  // bridge, pointed at the real functions as soon as they exist (the effect below). Held in a ref so a new function
  // identity never tears the connection down.
  const bridgeRef = useRef({
    restored: () => {},
    rowStale: (_sessionId: string) => {},
    countsStale: () => {},
    catalogStale: () => {},
    consoleConfigStale: () => {},
  });
  const hub = useAgentHub({
    // OP-1005 - a spam mark was undone: the row can come back, so re-read the folder (the tombstone stays the authority
    // until the store lifts it) and the counts.
    onSessionRestored: () => bridgeRef.current.restored(),
    // ADR-033 - a push or a refusal made one row's owner doubtful: re-read THAT row.
    onRowStale: (sessionId) => bridgeRef.current.rowStale(sessionId),
    onCountsStale: () => bridgeRef.current.countsStale(),
    onCatalogStale: () => bridgeRef.current.catalogStale(),
    onConsoleConfigStale: () => bridgeRef.current.consoleConfigStale(),
  });
  const { status } = hub;

  // Accrue the My Day live-activity feed from real store transitions.
  useActivityFeed();

  const loaders = useSessionLoaders();
  const {
    selectedFolder,
    sessionsLoading,
    selectFolder,
    loadMore,
    reloadSelected,
    refreshSelectedFolder,
    refreshRow,
    refreshCounts,
    requestCounts,
    refreshDrafts,
    requestCatalog,
    reconnect,
  } = loaders;

  const setStateDefs = useConversationStore((s) => s.setStateDefs);
  const setConsoleSettings = useConversationStore((s) => s.setConsoleSettings);
  const setDirectory = useAgentStore((s) => s.setDirectory);
  const scope = useAuthStore((s) => `${s.user?.tenantId ?? ''}|${s.user?.agentId ?? ''}`);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  // The selected folder's own paging state: this is what `sessionsError` / `folderLoading` read.
  const selectedPage = useConversationStore((s) => s.folderPages[selectedFolder]);
  const folderLoading = !!selectedPage?.loading;
  const folderError = !!selectedPage?.error;
  // Same meaning as before the folders: the selected list could not be loaded (non-blocking - SignalR still feeds).
  const sessionsError = folderError;

  const mode = useThemeStore((s) => s.mode);
  const lang = useLangStore((s) => s.lang);
  const dir = dirFor(lang);
  const tokens = useMemo(() => makeTokens({ mode }), [mode]);

  // ── Console config: the conversation statuses (state defs) + the console settings ─────────────────────────────────
  // Read on boot (per tenant+agent) and again - debounced - whenever `ReceiveConsoleConfigChanged` says either changed.
  // A newer read supersedes an older one still in flight, and a read started for another tenant+agent is dropped.
  const reloadSelectedRef = useRef(reloadSelected);
  reloadSelectedRef.current = reloadSelected;
  const consoleConfigSeqRef = useRef(0);
  const loadConsoleConfig = useCallback(async (): Promise<void> => {
    const mySeq = ++consoleConfigSeqRef.current;
    const myScope = scopeRef.current;
    const fresh = () => mySeq === consoleConfigSeqRef.current && myScope === scopeRef.current;
    await Promise.all([
      // ADR-017 D3 — the tenant's conversation statuses for the rail and the pickers. Best-effort: a failure keeps what
      // the console has (none on boot - the rail then says there are no statuses). [] is a real answer (PR-A: no
      // auto-seeded defaults any more).
      (async () => {
        try {
          const response = await axiosClient.get<ConversationStateDef[]>(API.SESSIONS.STATE_DEFS);
          if (fresh()) setStateDefs(response.data);
        } catch {
          /* non-blocking */
        }
      })(),
      // PR-A — the console settings. A DISPLAY preference only: a failed read forgets them (null), which shows the tag
      // folders (fail-soft) and says nothing about private/public visibility - that is the server's answer on every list.
      (async () => {
        try {
          const settings = await getConsoleSettings();
          if (!fresh()) return;
          const before = useConversationStore.getState().consoleSettings;
          setConsoleSettings(settings);
          // Private conversations were switched on or off: every list this console holds was filtered by the OLD rule,
          // so re-read the selected folder now rather than at the next poll. (The counts follow the push.)
          if (before && before.privateConversationsEffective !== settings.privateConversationsEffective) {
            void reloadSelectedRef.current();
          }
        } catch {
          if (fresh()) setConsoleSettings(null);
        }
      })(),
    ]);
  }, [setStateDefs, setConsoleSettings]);

  const consoleConfigTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** "The console config may have changed" (the push, or the store counter it bumps): debounced into ONE read. */
  const requestConsoleConfig = useCallback(() => {
    if (consoleConfigTimerRef.current) clearTimeout(consoleConfigTimerRef.current);
    consoleConfigTimerRef.current = setTimeout(() => {
      consoleConfigTimerRef.current = null;
      void loadConsoleConfig();
    }, CONSOLE_CONFIG_DEBOUNCE_MS);
  }, [loadConsoleConfig]);
  useEffect(
    () => () => {
      if (consoleConfigTimerRef.current) clearTimeout(consoleConfigTimerRef.current);
    },
    [],
  );
  // ReceiveConsoleConfigChanged bumped the store's counter (mirrors the tag catalog's catalogVersion).
  useEffect(
    () =>
      useConversationStore.subscribe((state, prev) => {
        if (state.consoleConfigVersion !== prev.consoleConfigVersion) requestConsoleConfig();
      }),
    [requestConsoleConfig],
  );

  useEffect(() => {
    bridgeRef.current = {
      restored: () => {
        void reloadSelected();
      },
      rowStale: (sessionId) => {
        void refreshRow(sessionId);
      },
      countsStale: requestCounts,
      catalogStale: requestCatalog,
      consoleConfigStale: requestConsoleConfig,
    };
  }, [reloadSelected, refreshRow, requestCounts, requestCatalog, requestConsoleConfig]);

  useEffect(() => {
    let cancelled = false;
    // The conversation statuses and the console settings, once per tenant+agent (see loadConsoleConfig).
    void loadConsoleConfig();
    // Fetch the tenant's agentId→displayName directory once, so the Conversation panel can render an
    // owner as a person instead of a GUID. Best-effort: on failure the panel says "name unknown" and
    // keeps the id in a tooltip. The Array.isArray guard matters because this endpoint returns a BARE
    // array — a paged envelope (or a blanket test mock) would otherwise reach setDirectory.
    (async () => {
      try {
        const response = await axiosClient.get<AgentDirectoryEntryDto[]>(API.AGENTS.DIRECTORY);
        if (!cancelled && Array.isArray(response.data)) setDirectory(response.data);
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on the signed-in tenant+agent: a different one has a different state-def set, settings and directory.
  }, [scope, loadConsoleConfig, setDirectory]);

  // OP-327 — reload on hub reconnect (events missed while offline). Skip the first 'connected' transition (the boot
  // load already covers it). The folder pages are forgotten first (the open row stays pinned, OP-372).
  const hasConnectedOnceRef = useRef(false);
  useEffect(() => {
    if (status !== 'connected') return;
    if (hasConnectedOnceRef.current) {
      reconnect();
      // A ReceiveConsoleConfigChanged sent while the hub was down is lost too.
      requestConsoleConfig();
    } else {
      hasConnectedOnceRef.current = true;
    }
  }, [status, reconnect, requestConsoleConfig]);

  // Every 60 s while the hub is up AND the tab is visible: the counts, the drafts feed and - when it is older than
  // FOLDER_STALE_MS - the SELECTED folder's list, so the badge (counts) and the rows cannot drift apart while no push
  // arrives. (A list the agent has scrolled past page 1 of is never replaced: its rows are re-read one by one instead -
  // `refreshSelectedFolder`.) A hidden tab skips its tick and catches up the moment it is shown again.
  const pollTick = useCallback(async (): Promise<'continue' | 'stop'> => {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
      await Promise.all([refreshCounts(), refreshDrafts(), refreshSelectedFolder()]);
    }
    return 'continue';
  }, [refreshCounts, refreshDrafts, refreshSelectedFolder]);
  usePoller({ enabled: status === 'connected', intervalMs: () => COUNTS_POLL_MS, resetKey: scope, tick: pollTick });
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && status === 'connected') {
        void refreshCounts();
        void refreshDrafts();
        void refreshSelectedFolder();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [status, refreshCounts, refreshDrafts, refreshSelectedFolder]);

  const ctx: ConsoleContextValue = useMemo(
    () => ({
      hub,
      reloadSessions: reloadSelected,
      selectedFolder,
      selectFolder,
      loadMore,
      folderLoading,
      folderError,
      refreshRow,
      refreshCounts,
      sessionsLoading,
      sessionsError,
    }),
    [
      hub,
      reloadSelected,
      selectedFolder,
      selectFolder,
      loadMore,
      folderLoading,
      folderError,
      refreshRow,
      refreshCounts,
      sessionsLoading,
      sessionsError,
    ],
  );

  return (
    <ConsoleContext.Provider value={ctx}>
      <div
        className="ac-root"
        dir={dir}
        key={mode}
        style={tokens as React.CSSProperties}
        data-testid="console-root"
      >
        <Rail />
        <div className="ac-main">
          <Topbar />
          <div className="ac-content">
            <Outlet />
          </div>
        </div>
      </div>
    </ConsoleContext.Provider>
  );
};
